import React, { Component } from 'react'
import Gallery from 'components/Gallery'
import VisitContainer from 'containers/VisitContainer'
import MonitorContainer from 'containers/MonitorContainer'
import EquipmentTestsContainer from 'containers/EquipmentTestsContainer'
import VisitCheckInContainer from 'containers/VisitCheckInContainer'
import WebRTCStatsContainer from 'containers/WebRTCStatsContainer'
import { Provider } from 'react-redux'
import configureStore from 'store'
import { HashRouter as Router, Route, Redirect } from 'react-router-dom'
import { addLocaleData } from 'react-intl'
import ConnectedIntlProvider from 'containers/ConnectedIntlProvider'
import { HASH_CHANGED } from 'actions/visits'
import queryString from 'query-string'
import { isString } from 'lodash'
import { isValidRedirectURL } from 'utils/url'
import { datadogLogs } from '@datadog/browser-logs'
import en from 'react-intl/locale-data/en'
import es from 'react-intl/locale-data/es'
import fr from 'react-intl/locale-data/fr'
import localeMapping, {CANADA, CANADA_DOMAIN, USA} from '../../translations'
addLocaleData([...en, ...es, ...fr])

import 'bootstrap/dist/css/bootstrap.css'
import 'normalize.css';
import '../styles/fonts.css'
import '../styles/typography.css'
import '../styles/bootstrap-overrides.css'

function setDatadogLogsGlobalContext(country, appointmentId, conferenceId) {
  let logGlobalContext = {
    visitURL: location.href,
    visitCountry: country
  }
  if (appointmentId) {
    logGlobalContext.appointmentId = appointmentId
  }
  if (conferenceId) {
    logGlobalContext.conferenceId = conferenceId
  }
  datadogLogs.setGlobalContext(logGlobalContext)
}

function stateFromQueryParams() {
  const queryParams = queryString.parse(location.hash.split('?')[1])
  const localeFromQueryParam = queryParams.locale
  const localeKey = localeFromQueryParam && ['en', 'es', 'fr'].includes(localeFromQueryParam) ? localeFromQueryParam : 'en'
  const jwtToken = queryParams.jwt
  const appointmentId = queryParams.appointment_id
  const conferenceId = queryParams.conference_id
  const inmateDeviceIds = queryParams['inmate_device_ids[]']
  const visitorDeviceIds = queryParams['visitor_device_ids[]']
  const deviceId = queryParams['device_id']
  let redirectURL
  if (isValidRedirectURL(queryParams['redirect_url'])) {
    redirectURL = queryParams['redirect_url']
  }
  const country = location.hostname.endsWith(CANADA_DOMAIN) ? CANADA : USA
  setDatadogLogsGlobalContext(country, appointmentId, conferenceId)

  return {
    visit: {
      jwtToken,
      appointmentId,
      conferenceId,
      inmateDeviceIds: isString(inmateDeviceIds) ? [inmateDeviceIds] : inmateDeviceIds,
      visitorDeviceIds: isString(visitorDeviceIds) ? [visitorDeviceIds] : visitorDeviceIds,
      deviceId: deviceId,
      redirectURL
    },
    lang: {
      messages: localeMapping[country][localeKey].messages,
      locale: localeMapping[country][localeKey].locale,
      country: country
    }
  }
}

const store = configureStore(stateFromQueryParams())

class Root extends Component {
  constructor(props) {
    super(props)
    this.handleHashChange = this.handleHashChange.bind(this)
  }

  handleHashChange(event) {
    const oldURL = new URL(event.oldURL)
    const newURL = new URL(event.newURL)
    // getting route part of hash without '/',
    // e.g. for URL "http://localhost:8080/#/visit?jwt=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzU" result will be '#visit'
    // added removal of '/' because after debugging on iOS there were cases when one of the URLs contained '#/visit' with '/'
    // but another contained '#visit' without '/' and routes did not matched in if statement below
    const oldHashRoute = oldURL.hash.split('?')[0].replace('/', '')
    const newHashRoute = newURL.hash.split('?')[0].replace('/', '')
    // If route remains the same but URL parameters changed reload the page. This should cover situation, when on iOS
    // after visit completed user does not close the tab in Safari. Then next visit will be opened in the same tab.
    if (oldHashRoute === newHashRoute) {
      window.location.reload()
    } else {
      store.dispatch({
        type: HASH_CHANGED,
        ...stateFromQueryParams()
      })
    }
  }

  componentDidMount() {
    window.addEventListener("hashchange", this.handleHashChange);
  }

  componentWillUnmount() {
    window.removeEventListener("hashchange", this.handleHashChange);
  }

  render() {
    return (
      <Provider store={store}>
        <ConnectedIntlProvider>
          <Router>
            <Route path="/" exact render={() => <Redirect to='/gallery' />} />
            <Route path="/visit" component={VisitContainer} />
            <Route path="/gallery" component={Gallery} />
            <Route path="/monitor" component={MonitorContainer} />
            <Route path="/check-in" component={VisitCheckInContainer} />
            <Route path="/webrtc-stats" component={WebRTCStatsContainer} />
            <Route path="/tests" component={EquipmentTestsContainer} />
            <Route path="/tests-rerun" render={
              ({ location: { search } }) => {
                return (
                  <Redirect to={`/tests${search}`} />
                )
              }
            } />
          </Router>
        </ConnectedIntlProvider>
      </Provider>
    )
  }
}


export default Root
